import BodyText from '@lyra/core/components/BodyText'
import DataWithLabel from '@lyra/core/components/DataWithLabel'
import Section, { SectionProps } from '@lyra/core/components/Section'
import Table, { createColumnHelper, TableData } from '@lyra/core/components/Table'
import Tooltip from '@lyra/core/components/Tooltip'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import TokenIcon from '@lyra/web/components/common/TokenIcon'
import { MARKET_ICON_SIZE } from '@lyra/web/constants/layout'
import { PageId } from '@lyra/web/constants/pages'
import { collateralConfig, CollateralId, TokenId } from '@lyra/web/constants/tokens'
import useAllCollateralData from '@lyra/web/hooks/useAllCollateralData'
import useAuth from '@lyra/web/hooks/useAuth'
import useCollaterals from '@lyra/web/hooks/useCollaterals'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getPagePath } from '@lyra/web/utils/pages'
import { getSubaccountCollateral } from '@lyra/web/utils/subaccounts'
import {
  formatCollateralBalance,
  formatTokenName,
  formatTokenSymbol,
  getDefaultDepositTokenForToken,
  getTokenForCollateral,
  sortCollaterals,
} from '@lyra/web/utils/tokens'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import DepositButton from '../common/DepositButton'
import MarginUsageDataWithLabel from '../common/MarginUsageProgress'
import SignInButton from '../common/SignInButton'
import SubaccountsDropdown from '../common/SubaccountsDropdown'
import TransferButton from '../common/TransferButton'

type BorrowTableData = TableData<{
  collateralId: CollateralId
  totalSupply: number
  tvl: number
  ltv: number
  balance: number
  lockedBalance: number
  margin: number
}>

type Props = {
  showSubaccountSwitch?: boolean
  showConnectPrompt?: boolean
} & SectionProps

const BorrowSection = ({ showSubaccountSwitch, showConnectPrompt, ...sectionProps }: Props) => {
  const { subaccountData, subaccounts, switchSubaccount } = useSubaccount()
  const { data: collateralDatas, isLoading, error } = useAllCollateralData()

  const subaccount = subaccountData?.subaccount

  const router = useRouter()

  const usdcCollateralData = useMemo(() => {
    return collateralDatas ? collateralDatas[CollateralId.USDC] : undefined
  }, [collateralDatas])

  const collaterals = useCollaterals()

  const columnHelper = createColumnHelper<BorrowTableData>()

  const usdcBorrowed = subaccountData ? subaccountData.usdcBorrowed : 0
  const usdcAvailableToBorrow = subaccountData ? subaccountData.usdcAvailableToBorrow : 0

  const borrowApy = usdcCollateralData ? +usdcCollateralData.borrow_apy : 0

  const rows: BorrowTableData[] = useMemo(() => {
    if (!collateralDatas) {
      return []
    }

    // borrowing unavailable on PM accounts
    if (subaccountData?.subaccount.margin_type === 'PM') {
      return []
    }

    const collateralIds = sortCollaterals(
      (Object.keys(collateralDatas) as CollateralId[]).filter(
        (collateralId) =>
          collateralId !== CollateralId.USDC && !collateralConfig[collateralId].isUnlisted
      )
    )

    return filterNulls(
      collateralIds.map((collateralId) => {
        const data = collateralDatas[collateralId]
        if (!data) {
          return null
        }

        const spotPrice = collaterals[collateralId].spotPrice

        const collateralObj = subaccountData
          ? getSubaccountCollateral(subaccountData.subaccount, collateralId)
          : null

        const lockedBalance = subaccountData
          ? subaccountData.collateralBalances[collateralId].lockedBalance
          : 0

        const ltv = +data.srm_im_discount

        const balance = subaccountData ? subaccountData.collateralBalances[collateralId].balance : 0
        const balanceValue = balance * spotPrice

        const totalSupply = +data.total_supply
        const tvl = totalSupply * spotPrice

        return {
          collateralId,
          totalSupply,
          tvl,
          ltv,
          balance,
          balanceValue,
          lockedBalance,
          margin: collateralObj ? +collateralObj.initial_margin : 0,
          onPress: () => router.push(getPagePath({ page: PageId.Collateral, collateralId })),
        }
      })
    ).sort((a, b) => b.balanceValue - a.balanceValue)
  }, [subaccountData, collaterals, collateralDatas, router])

  const hasLockedBalance = useMemo(() => {
    return rows.some((row) => row.lockedBalance > 0)
  }, [rows])

  const hasMargin = useMemo(() => {
    return rows.some((row) => row.margin > 0)
  }, [rows])

  const columns = useMemo(
    () =>
      filterNulls([
        columnHelper.accessor('collateralId', {
          header: 'Collateral',
          meta: {
            minWidth: 180,
          },
          cell: ({
            row: {
              original: { collateralId },
            },
          }) => {
            return (
              <XStack gap="$1.5" alignItems="center">
                <TokenIcon symbol={collateralId} size={MARKET_ICON_SIZE} />
                <YStack>
                  <BodyText>{formatTokenSymbol(collateralId)}</BodyText>
                  <BodyText color="secondary">{formatTokenName(collateralId)}</BodyText>
                </YStack>
              </XStack>
            )
          },
        }),
        columnHelper.accessor('tvl', {
          header: 'TVL',
          meta: {
            minWidth: 100,
          },
          cell: ({
            row: {
              original: { tvl, totalSupply, collateralId },
            },
          }) => {
            // TODO: remove this once we have the correct TVL for OP @vikrvm
            return (
              <YStack>
                <BodyText>
                  {collateralId === CollateralId.OP
                    ? '-'
                    : formatCollateralBalance(totalSupply, collateralId, { truncate: true })}
                </BodyText>
                <BodyText color="secondary">
                  {collateralId === CollateralId.OP ? '-' : formatUSD(tvl, { dps: 0 })}
                </BodyText>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('ltv', {
          header: 'LTV',
          meta: {
            minWidth: 100,
          },
          cell: ({
            row: {
              original: { ltv, collateralId },
            },
          }) => {
            // TODO: remove this once we have the correct LTV for OP @vikrvm
            return collateralId === CollateralId.OP ? (
              <BodyText>{formatPercentage(0.51)}</BodyText>
            ) : (
              <BodyText>{formatPercentage(ltv)}</BodyText>
            )
          },
        }),
        columnHelper.accessor('balance', {
          header: 'Balance',
          meta: {
            minWidth: 100,
          },
          cell: ({
            row: {
              original: { balance, collateralId },
            },
          }) => {
            return (
              <BodyText color={balance ? 'primary' : 'secondary'}>
                {balance ? formatCollateralBalance(balance, collateralId) : '—'}
              </BodyText>
            )
          },
        }),
        hasMargin
          ? columnHelper.accessor('margin', {
              header: 'Margin',
              meta: {
                minWidth: 100,
              },
              cell: ({
                row: {
                  original: { margin },
                },
              }) => {
                return (
                  <BodyText color={margin ? 'primary' : 'secondary'}>
                    {margin ? formatUSD(margin) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
        hasLockedBalance
          ? columnHelper.accessor('lockedBalance', {
              header: 'Locked',
              meta: {
                minWidth: 100,
              },
              cell: ({
                row: {
                  original: { lockedBalance, collateralId },
                },
              }) => {
                return (
                  <BodyText color={lockedBalance ? 'primary' : 'secondary'}>
                    {lockedBalance ? formatCollateralBalance(lockedBalance, collateralId) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
        columnHelper.accessor('action', {
          header: 'Action',
          meta: {
            minWidth: 170,
            maxWidth: 170,
            justifyContent: 'flex-end',
          },
          cell: ({
            row: {
              original: { collateralId },
            },
          }) => {
            return (
              <XStack gap="$1" onPress={(e) => e.stopPropagation()}>
                <DepositButton
                  defaultDepositToken={getDefaultDepositTokenForToken(
                    getTokenForCollateral(collateralId)
                  )}
                  isCta={false}
                />
                <TransferButton
                  defaultToSubaccountId={subaccount?.subaccount_id}
                  asset={getTokenForCollateral(collateralId)}
                />
              </XStack>
            )
          },
        }),
      ]),
    [columnHelper, subaccount, hasLockedBalance, hasMargin]
  )

  const isPmAccount = subaccountData?.subaccount.margin_type === 'PM'

  const { isAuthenticated } = useAuth()

  const isBorrowDisabled = !subaccount || !usdcAvailableToBorrow

  return (
    <Section {...sectionProps}>
      <Section.Header
        title="Borrow USDC"
        rightContent={
          showSubaccountSwitch && Object.values(subaccounts).length > 1 && subaccount ? (
            <SubaccountsDropdown
              subaccountId={subaccount.subaccount_id}
              onSelect={switchSubaccount}
            />
          ) : null
        }
      />
      {!isAuthenticated && showConnectPrompt ? (
        <Section.XStack>
          <SignInButton isCta size="lg" minWidth={140} />
        </Section.XStack>
      ) : !isPmAccount ? (
        <>
          <Section.Grid minColWidth={160} maxColWidth={180}>
            {subaccountData ? (
              <DataWithLabel
                label="Collateral Value"
                value={formatUSD(subaccountData ? subaccountData.collateralValue : 0)}
              />
            ) : null}
            {usdcAvailableToBorrow > 0 ? (
              <DataWithLabel
                label="Available To Borrow"
                value={formatCollateralBalance(usdcAvailableToBorrow, CollateralId.USDC, {
                  truncate: true,
                })}
              />
            ) : null}
            {usdcBorrowed > 0 ? (
              <DataWithLabel
                label="Borrowed"
                value={formatCollateralBalance(usdcBorrowed, CollateralId.USDC, { truncate: true })}
              />
            ) : null}
            {subaccountData && subaccountData.marginUtilization > 0 ? (
              <MarginUsageDataWithLabel marginUtilization={subaccountData.marginUtilization} />
            ) : null}
            <DataWithLabel
              label="Borrow APY"
              value={isLoading ? '...' : formatPercentage(borrowApy)}
            />
          </Section.Grid>
          {isAuthenticated ? (
            <Section.XStack>
              <Tooltip
                content={
                  isBorrowDisabled
                    ? 'Deposit and transfer collaterals like ETH and wBTC into your trading account to borrow USDC against them.'
                    : null
                }
                trigger={
                  <TransferButton
                    label="Borrow USDC"
                    defaultToSubaccountId="funding"
                    fromSubaccountId={subaccount?.subaccount_id}
                    asset={TokenId.USDC}
                    isDisabled={isBorrowDisabled}
                    color="cta"
                    size="lg"
                    minWidth={140}
                    useCase="borrow"
                  />
                }
              />
            </Section.XStack>
          ) : null}
        </>
      ) : null}
      <Section.Table>
        <Table
          size="lg"
          columns={columns}
          data={rows}
          emptyMessage={
            isPmAccount
              ? 'Borrowing is not available on portfolio margin accounts'
              : isLoading
              ? '...'
              : error
              ? 'Failed to load balances'
              : 'Something went wrong'
          }
        />
      </Section.Table>
    </Section>
  )
}

export default BorrowSection
